/**
 * HALO - Notifications Settings Section
 * Push notification and alert preferences
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, BellRing, Users, Calendar, Coffee, Clock, CheckCircle2, AlertCircle, BellOff } from "lucide-react";
import { Card } from "@/components/ui/card";
import { getNotificationPreferences, updateNotificationPreferences, requestPushNotificationPermission, NotificationPreferences } from "@/lib/supabase";

interface NotificationsSectionProps {
  userId: string;
}

interface NotificationOption {
  key: keyof NotificationPreferences;
  label: string;
  description: string;
  icon: React.ElementType;
  premium?: boolean;
}

const notificationOptions: NotificationOption[] = [
  {
    key: "wellness_alerts",
    label: "Wellness Alerts",
    description: "Get notified when high fatigue or poor posture is detected",
    icon: BellRing,
  },
  {
    key: "team_activity",
    label: "Team Activity",
    description: "Notifications when team members come online or go offline",
    icon: Users,
  },
  {
    key: "daily_summary",
    label: "Daily Summary",
    description: "Receive a daily summary of your wellness metrics",
    icon: Calendar,
    premium: true,
  },
  {
    key: "break_reminders",
    label: "Break Reminders",
    description: "Gentle reminders to take breaks based on your fatigue levels",
    icon: Coffee,
  },
];

export function NotificationsSection({ userId }: NotificationsSectionProps) {
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [pushPermission, setPushPermission] = useState<NotificationPermission>("default");
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    loadPreferences();
    checkPushPermission();
  }, [userId]);

  const checkPushPermission = () => {
    if ("Notification" in window) {
      setPushPermission(Notification.permission);
    }
  };

  const loadPreferences = async () => {
    try {
      const data = await getNotificationPreferences(userId);
      if (data) {
        setPreferences(data);
      }
    } catch (err) {
      console.error("[NotificationsSection] Error loading preferences:", err);
      setMessage({ type: "error", text: "Failed to load notification preferences" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestPushPermission = async () => {
    const result = await requestPushNotificationPermission();
    if (result.success) {
      setPushPermission("granted");
      setMessage({ type: "success", text: "Push notifications enabled successfully" });
    } else {
      setMessage({ type: "error", text: result.error || "Failed to enable notifications" });
    }
  };

  const handleToggle = (key: keyof NotificationPreferences) => {
    if (!preferences) return;

    setPreferences((prev) => {
      if (!prev) return prev;
      return { ...prev, [key]: !prev[key] };
    });
  };

  const handleSave = async () => {
    if (!preferences) return;

    setIsSaving(true);
    setMessage(null);

    try {
      const result = await updateNotificationPreferences(userId, preferences);

      if (result.success) {
        setMessage({ type: "success", text: "Notification preferences updated" });
      } else {
        setMessage({ type: "error", text: result.error || "Failed to update preferences" });
      }
    } catch (err) {
      console.error("[NotificationsSection] Error saving preferences:", err);
      setMessage({ type: "error", text: "Failed to update preferences" });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!preferences) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-signal-crimson mx-auto mb-4" />
        <p className="text-muted-slate">Failed to load notification preferences</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Push Notification Permission Card */}
      <Card className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Bell className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Push Notifications</h3>
            <p className="text-sm text-muted-slate">Enable browser notifications for real-time alerts</p>
          </div>
        </div>

        <div className="space-y-4">
          {pushPermission === "granted" ? (
            <div className="p-4 rounded-xl bg-signal-green/10 border border-signal-green/20">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-signal-green" />
                <span className="text-sm font-medium text-signal-green">
                  Push notifications are enabled
                </span>
              </div>
            </div>
          ) : pushPermission === "denied" ? (
            <div className="p-4 rounded-xl bg-signal-crimson/10 border border-signal-crimson/20">
              <div className="flex items-start gap-3">
                <BellOff className="w-5 h-5 text-signal-crimson mt-0.5" />
                <div>
                  <span className="text-sm font-medium text-signal-crimson block">
                    Push notifications are blocked
                  </span>
                  <p className="text-sm text-muted-slate mt-1">
                    Please enable notifications in your browser settings to receive alerts.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 rounded-xl bg-surface-elevated border border-glass">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Bell className="w-5 h-5 text-muted-slate mt-0.5" />
                  <div>
                    <span className="text-sm font-medium text-foreground block">
                      Enable Push Notifications
                    </span>
                    <p className="text-sm text-muted-slate mt-1">
                      Get notified even when the app is in the background
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleRequestPushPermission}
                  className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg text-sm font-medium transition-colors shrink-0"
                >
                  Enable
                </button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Notification Preferences */}
      <Card className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-full bg-surface-elevated flex items-center justify-center">
            <BellRing className="w-6 h-6 text-foreground" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Notification Types</h3>
            <p className="text-sm text-muted-slate">Choose what you want to be notified about</p>
          </div>
        </div>

        <div className="space-y-3">
          {notificationOptions.map((option) => {
            const Icon = option.icon;
            const isEnabled = preferences[option.key];

            return (
              <motion.div
                key={option.key}
                whileHover={{ scale: 1.01 }}
                className={`p-4 rounded-xl border transition-colors cursor-pointer ${
                  isEnabled
                    ? "bg-primary/5 border border-primary/15"
                    : "bg-surface-elevated border border-glass-grey"
                }`}
                onClick={() => handleToggle(option.key)}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                      isEnabled ? "bg-primary/10 text-primary" : "bg-muted-slate/10 text-muted-slate"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-semibold text-foreground">{option.label}</h4>
                        {option.premium && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                            Pro
                          </span>
                        )}
                      </div>
                      <div
                        className={`w-12 h-6 rounded-full transition-colors relative ${
                          isEnabled ? "bg-primary" : "bg-muted-slate/30"
                        }`}
                      >
                        <motion.div
                          className="w-5 h-5 bg-foreground rounded-full absolute top-0.5"
                          animate={{ left: isEnabled ? "26px" : "2px" }}
                          transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        />
                      </div>
                    </div>
                    <p className="text-sm text-muted-slate mt-1">{option.description}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Quiet Hours */}
        <AnimatePresence>
          {(preferences.wellness_alerts || preferences.team_activity || preferences.break_reminders) && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-6 pt-6 border-t border-glass"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-10 h-10 rounded-lg bg-surface-elevated flex items-center justify-center">
                  <Clock className="w-5 h-5 text-foreground" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground">Quiet Hours</h4>
                  <p className="text-sm text-muted-slate">Temporarily pause notifications</p>
                </div>
              </div>

              <motion.div
                whileHover={{ scale: 1.01 }}
                className={`p-4 rounded-xl border transition-colors cursor-pointer ${
                  preferences.quiet_hours_enabled
                    ? "bg-signal-amber/5 border-signal-amber/20"
                    : "bg-surface-elevated border border-glass-grey"
                }`}
                onClick={() => handleToggle("quiet_hours_enabled")}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="text-sm font-medium text-foreground">Enable Quiet Hours</h5>
                    <p className="text-sm text-muted-slate mt-1">
                      Pause all notifications during specific hours
                    </p>
                  </div>
                  <div
                    className={`w-12 h-6 rounded-full transition-colors relative ${
                      preferences.quiet_hours_enabled ? "bg-signal-amber" : "bg-muted-slate/30"
                    }`}
                  >
                    <motion.div
                      className="w-5 h-5 bg-foreground rounded-full absolute top-0.5"
                      animate={{ left: preferences.quiet_hours_enabled ? "26px" : "2px" }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mt-6 p-4 rounded-xl flex items-center gap-3 ${
              message.type === "success"
                ? "bg-signal-green/10 text-signal-green"
                : "bg-signal-crimson/10 text-signal-crimson"
            }`}
          >
            {message.type === "success" ? (
              <CheckCircle2 className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span className="text-sm font-medium">{message.text}</span>
          </motion.div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2.5 bg-primary hover:bg-primary/90 disabled:bg-primary/50 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-colors"
          >
            {isSaving ? "Saving..." : "Save Preferences"}
          </button>
        </div>
      </Card>
    </motion.div>
  );
}
